import React from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
    Chip,
    Paper
} from '@mui/material';
import {
    BugReport as BugIcon,
    ErrorOutline as OpenIcon,
    Autorenew as InProgressIcon,
    CheckCircleOutline as ResolvedIcon,
    VerifiedUser as ClosedIcon,
    RocketLaunch as DeployedIcon,
    Science as TestingIcon,
    ReportProblem as FixNotWorkingIcon,
    PendingActions as BacklogIcon,
    Block as DefaulterIcon
} from '@mui/icons-material';

/**
 * Shared Issue Tracker Summary component used on both the Issues page
 * and the Release Dashboard page.
 *
 * Props:
 *   stats: {
 *     total_issues: number,
 *     open: number,
 *     in_progress: number,
 *     resolved: number,
 *     fix_deployed: number,
 *     closed: number,
 *     by_priority: { critical: n, high: n, medium: n, low: n },
 *     by_module: [ { module_name, count } ]
 *   }
 *   onTileClick: (statusFilterValue: string|null) => void  — called when a tile is clicked
 *   activeFilter: string|null  — currently active status filter value (e.g. 'Open')
 *   onModuleClick: (moduleName: string) => void  — called when a module chip is clicked
 *   activeModuleFilter: string  — currently active module filter (module name or '')
 */

// Map tile keys to the status filter values used by IssueList / API
const STATUS_KEY_TO_FILTER = {
    total_issues: null,        // show all
    open: 'Open',
    in_progress: 'In Development',
    resolved: 'Resolved',
    fix_deployed: 'Fix Deployed',
    testing_in_progress: 'Testing in progress',
    fix_not_working: 'Fix not working',
    closed: 'Closed',
    backlog: 'Backlog',
    defaulter: 'Defaulter',
};

// Reverse map: filter value → tile key
const FILTER_TO_TILE_KEY = Object.fromEntries(
    Object.entries(STATUS_KEY_TO_FILTER)
        .filter(([, v]) => v !== null)
        .map(([k, v]) => [v, k])
);

const TILE_CONFIG = [
    {
        key: 'total_issues',
        label: 'Total Issues',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        icon: <BugIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'open',
        label: 'Open',
        gradient: 'linear-gradient(135deg, #f44336 0%, #e91e63 100%)',
        icon: <OpenIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'in_progress',
        label: 'In Development',
        gradient: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
        icon: <InProgressIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'resolved',
        label: 'Resolved',
        gradient: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
        icon: <ResolvedIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'fix_deployed',
        label: 'Fix Deployed',
        gradient: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
        icon: <DeployedIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'testing_in_progress',
        label: 'Testing in progress',
        gradient: 'linear-gradient(135deg, #9c27b0 0%, #6a1b9a 100%)',
        icon: <TestingIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'fix_not_working',
        label: 'Fix not working',
        gradient: 'linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)',
        icon: <FixNotWorkingIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'closed',
        label: 'Closed',
        gradient: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)',
        icon: <ClosedIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'backlog',
        label: 'Backlog',
        gradient: 'linear-gradient(135deg, #78909c 0%, #546e7a 100%)',
        icon: <BacklogIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
    {
        key: 'defaulter',
        label: 'Defaulter',
        gradient: 'linear-gradient(135deg, #880e4f 0%, #560027 100%)',
        icon: <DefaulterIcon sx={{ fontSize: 24, opacity: 0.85 }} />,
    },
];

const PRIORITY_COLORS = {
    critical: { bg: '#d32f2f', color: '#fff' },
    high: { bg: '#e65100', color: '#fff' },
    medium: { bg: '#f57c00', color: '#fff' },
    low: { bg: '#388e3c', color: '#fff' },
};

const IssueTrackerSummary = ({ stats, onTileClick, activeFilter, onModuleClick, activeModuleFilter }) => {
    if (!stats || stats.total_issues === 0) return null;

    const activeTileKey = activeFilter ? FILTER_TO_TILE_KEY[activeFilter] : null;
    const isClickable = !!onTileClick;
    const isModuleClickable = !!onModuleClick;

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
                Issue Tracker Summary
            </Typography>

            {/* Status Tiles */}
            <Grid container spacing={1} sx={{ flexWrap: 'nowrap' }}>
                {TILE_CONFIG.map((tile) => (
                    <Grid item xs key={tile.key} sx={{ minWidth: 0 }}>
                        <Card
                            onClick={isClickable ? () => onTileClick(STATUS_KEY_TO_FILTER[tile.key]) : undefined}
                            sx={{
                                background: tile.gradient,
                                color: '#fff',
                                borderRadius: 1.5,
                                boxShadow: activeTileKey === tile.key
                                    ? '0 0 0 3px #fff, 0 0 0 5px rgba(25,118,210,0.7)'
                                    : '0 2px 8px 0 rgba(0,0,0,0.12)',
                                cursor: isClickable ? 'pointer' : 'default',
                                transition: 'transform 0.15s, box-shadow 0.15s',
                                '&:hover': isClickable ? {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 16px 0 rgba(0,0,0,0.24)',
                                } : {},
                            }}
                        >
                            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 700, lineHeight: 1.2 }}
                                        >
                                            {stats[tile.key] ?? 0}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ opacity: 0.9, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                                        >
                                            {tile.label}
                                        </Typography>
                                    </Box>
                                    {tile.icon}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Issues by Priority */}
            {stats.by_priority &&
                Object.keys(stats.by_priority).length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Issues by Priority
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(stats.by_priority).map(
                                ([priority, count]) => {
                                    const key = priority.toLowerCase();
                                    const colors = PRIORITY_COLORS[key] || {
                                        bg: '#757575',
                                        color: '#fff',
                                    };
                                    return (
                                        <Chip
                                            key={priority}
                                            label={`${priority.charAt(0).toUpperCase() + priority.slice(1)}: ${count}`}
                                            size="small"
                                            sx={{
                                                bgcolor: colors.bg,
                                                color: colors.color,
                                                fontWeight: 600,
                                            }}
                                        />
                                    );
                                }
                            )}
                        </Box>
                    </Box>
                )}

            {/* Issues by Module */}
            {stats.by_module && stats.by_module.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Issues by Module
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {stats.by_module.map((item) => {
                            const isActive = activeModuleFilter && activeModuleFilter === item.module_name;
                            return (
                                <Chip
                                    key={item.module_name}
                                    label={`${item.module_name}: ${item.count}`}
                                    size="small"
                                    variant={isActive ? 'filled' : 'outlined'}
                                    color="primary"
                                    onClick={isModuleClickable ? () => onModuleClick(item.module_name) : undefined}
                                    sx={{
                                        fontWeight: 600,
                                        cursor: isModuleClickable ? 'pointer' : 'default',
                                        transition: 'all 0.15s',
                                        ...(isActive && {
                                            bgcolor: 'primary.main',
                                            color: '#fff',
                                            boxShadow: '0 0 0 2px rgba(25,118,210,0.4)',
                                        }),
                                        ...(!isActive && isModuleClickable && {
                                            '&:hover': {
                                                bgcolor: 'primary.light',
                                                color: '#fff',
                                            },
                                        }),
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Paper>
    );
};

export default IssueTrackerSummary;
